import React, { createContext, useContext, useState, useEffect } from 'react';

// API base URL configuration
const API_BASE = process.env.REACT_APP_API_BASE_URL || 
  (process.env.NODE_ENV === 'production' 
    ? `${window.location.origin}/api`
    : 'http://localhost:3069/api');

// Create Auth Context
const AuthContext = createContext(null);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state on mount
  useEffect(() => {
    initializeAuth();
  }, []);

  // Set up automatic token refresh
  useEffect(() => {
    if (isAuthenticated) {
      const refreshInterval = setInterval(() => {
        handleTokenRefresh();
      }, getRefreshInterval());

      return () => clearInterval(refreshInterval);
    }
  }, [isAuthenticated]);

  /**
   * Initialize authentication state
   */
  const initializeAuth = async () => {
    try {
      setLoading(true);
      
      // Check if we have a refresh token
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        setLoading(false);
        return;
      }

      // Try to refresh the access token
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
        credentials: 'include' // Include cookies
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
      } else {
        // Refresh failed, clear stored token
        localStorage.removeItem('refreshToken');
      }
    } catch (error) {
      console.error('Auth initialization error:', error);
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Login function
   */
  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, rememberMe }),
        credentials: 'include' // Include cookies
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      // Store refresh token
      localStorage.setItem('refreshToken', data.refreshToken);
      
      // Update auth state
      setUser(data.user);
      setIsAuthenticated(true);

      return data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  /**
   * Logout function
   */
  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (refreshToken) {
        // Call logout endpoint
        await fetch(`${API_BASE}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refreshToken }),
          credentials: 'include'
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API call success
      localStorage.removeItem('refreshToken');
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear access token cookie
      await fetch(`${API_BASE}/auth/clear-token`, {
        method: 'POST',
        credentials: 'include'
      });
    }
  };

  /**
   * Handle automatic token refresh
   */
  const handleTokenRefresh = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        logout();
        return;
      }

      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        // Refresh failed, logout user
        console.warn('Token refresh failed, logging out user');
        logout();
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      logout();
    }
  };

  /**
   * Change password function
   */
  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    try {
      const response = await fetch(`${API_BASE}/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Password change failed');
      }

      return data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  /**
   * Verify current authentication status
   */
  const verifyAuth = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/verify`, {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
        return true;
      } else {
        setUser(null);
        setIsAuthenticated(false);
        return false;
      }
    } catch (error) {
      console.error('Auth verification error:', error);
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  /**
   * Get refresh interval based on token expiry
   */
  const getRefreshInterval = () => {
    // Get token expiry from environment or default to 2 hours
    const expiry = process.env.REACT_APP_JWT_ACCESS_TOKEN_EXPIRY || '2h';
    
    if (expiry.includes('h')) {
      const hours = parseInt(expiry);
      return (hours * 60 - 5) * 60 * 1000; // Refresh 5 minutes before expiry
    }
    
    if (expiry.includes('m')) {
      const minutes = parseInt(expiry);
      return Math.max((minutes - 2) * 60 * 1000, 60000); // Refresh 2 minutes before expiry, minimum 1 minute
    }
    
    // Default: refresh every 115 minutes (5 minutes before 2 hour expiry)
    return 115 * 60 * 1000;
  };

  /**
   * Check if user has specific role
   */
  const hasRole = (role) => {
    return user?.role === role;
  };

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  /**
   * Check if user is admin or super admin
   */
  const isAdmin = () => {
    return hasAnyRole(['admin', 'super_admin']);
  };

  /**
   * Check if user is super admin
   */
  const isSuperAdmin = () => {
    return hasRole('super_admin');
  };

  // Context value
  const value = {
    // State
    user,
    loading,
    isAuthenticated,
    
    // Actions
    login,
    logout,
    changePassword,
    verifyAuth,
    
    // Utilities
    hasRole,
    hasAnyRole,
    isAdmin,
    isSuperAdmin,
    
    // Refresh function (for manual refresh)
    refreshAuth: handleTokenRefresh
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Higher-order component for protected routes
export const withAuth = (Component) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
      return <div>Loading...</div>; // Or your loading component
    }

    if (!isAuthenticated) {
      return <div>Please log in to access this page.</div>; // Or redirect to login
    }

    return <Component {...props} />;
  };
};

export default AuthProvider;
